/// <amd-module name="Release/Commands/Dialogs/FolderDetailsDialog" />
import * as React from "react";

import { Dialog } from "VSSUI/Dialog";
import { TextField, TextFieldWithMessage } from "VSSUI/TextField";

import { format, equals } from "VSS/Core/Util/String";
import { VssComponent, IVssComponentProperties, Components } from "VSS/Platform/Layout";

import * as Resources from "Release/Commands/Dialogs/Resources";

export interface IFolderDetailsDialogProps extends IVssComponentProperties {
    title: string;
    name?: string;
    parentPath: string;
    parentButtonId?: string;
    onFolderUpdated?: (newFolderPath: string) => any;
    onClose: () => any;
}

interface IFolderDetailsDialogState {
    name: string;
    nameErrorMessage: string;
}

export class FolderDetailsDialog extends VssComponent<IFolderDetailsDialogProps, IFolderDetailsDialogState>{

    constructor(props: IFolderDetailsDialogProps) {
        super(props);

        this.state = {
            name: this.props.name || "",
            nameErrorMessage: this.props.name ? "" : Resources.CannotBeEmptyErrorMessage
        };
    }

    public render(): React.ReactNode {
        return (
            <Dialog
                titleProps={{ text: this.props.title }}
                onDismiss={this._onDialogClose}
                defaultActiveElement=".folder-name-input"
                calloutContentClassName="release-command-dialog"
                footerButtonProps={[
                    {
                        onClick: this._onDialogClose,
                        text: Resources.Cancel
                    },
                    {
                        onClick: this._onOkClick,
                        disabled: !!this.state.nameErrorMessage,
                        primary: true,
                        text: Resources.Ok
                    }
                ]}>
                <TextField
                    className="folder-text-field"
                    label={Resources.FolderPathTextboxLabel}
                    value={this.props.parentPath}
                    disabled={true} />
                <TextFieldWithMessage
                    textFieldProps={{
                        label: Resources.NameLabel,
                        value: this.state.name,
                        inputClassName: "folder-name-input",
                        onChange: this._onNameChanged
                    }}
                    message={this.state.nameErrorMessage}
                />
            </Dialog>
        );
    }

    private _onNameChanged = (event: React.ChangeEvent<HTMLInputElement>, newValue: string): void => {
        let errorMessage = "";
        if (!newValue) {
            errorMessage = Resources.CannotBeEmptyErrorMessage;
        }
        else {
            const invalidCharacters = "<>|\\:$@\"/?*";
            for (let i = 0; i < invalidCharacters.length; i++) {
                if (newValue.indexOf(invalidCharacters[i]) !== -1) {
                    errorMessage = format(Resources.UnallowedCharactersErrorMessage, invalidCharacters);
                }
            }
        }

        this.setState({
            name: newValue,
            nameErrorMessage: errorMessage
        });
    }

    private _closeDialog() {
        if (!!this.props.onClose) {
            this.props.onClose();
        }

        if (!!this.props.parentButtonId) {
            let parentComp = document.getElementById("__bolt-" + this.props.parentButtonId);
            if (parentComp) {
                parentComp.focus();
            }
        }
    }

    private _onDialogClose = (): void => {
        this._closeDialog();
    }

    private _onOkClick = (): void => {
        this.props.onClose();

        let newFolderPath: string = this.props.parentPath;
        if (this._isRootPath(this.props.parentPath)) {
            newFolderPath = newFolderPath + this.state.name;
        }
        else {
            newFolderPath = newFolderPath + "\\" + this.state.name;
        }

        if (this.props.onFolderUpdated) {
            this.props.onFolderUpdated(newFolderPath);
        }
    }

    private _isRootPath(folderPath: string): boolean {
        if (!folderPath) {
            return true;
        }

        if (equals(folderPath, "\\", true)) {
            return true;
        }

        return false;
    }
}

Components.add("release-folder-dialog", FolderDetailsDialog);